import { useTranslation } from "@sagot/i18n";
import { Button, Field, Icon, Input, Select } from "@sagot/ui";
import { useForm } from "react-hook-form";
import { z } from "zod";
import { useErrorMessage } from "../../../lib/api-errors";
import { zodResolver } from "../../../lib/zod-resolver";
import type { DiscoveryResult } from "../shared/types";
import type { PanelProps } from "./text-panels";
import { useDiscoverSite, type WebsiteInput } from "./use-knowledge";

const K = "deployKnowledge.knowledge.add";
/** Crawl scope → `max_pages` (the API has no depth field; see report). */
export const CRAWL_SCOPES = [
  { pages: 1, key: "scopeOne" },
  { pages: 25, key: "scopeLinked" },
  { pages: 100, key: "scopeSection" },
  { pages: 500, key: "scopeSite" },
] as const;
const DEFAULT_SCOPE = 25;
const URL_MAX = 2083;
const PATTERN_MAX = 300;

export function splitPatterns(value: string): string[] {
  return value
    .split(",")
    .map((part) => part.trim())
    .filter((part) => part.length > 0 && part.length <= PATTERN_MAX);
}

function DiscoverySummary({ result }: { result: DiscoveryResult }) {
  const { t } = useTranslation();
  return (
    <div className="rounded-md border border-line bg-surface-2 px-3 py-2.5 text-xs" aria-live="polite" data-testid="crawl-preview">
      <p className="text-sm font-bold text-fg">
        {result.capped ? t(`${K}.previewCapped`, { count: result.page_count }) : t(`${K}.previewTitle`, { count: result.page_count })}
      </p>
      <ul className="mt-1.5 flex flex-wrap gap-x-4 gap-y-1 text-fg-muted">
        <li className="flex items-center gap-1">
          <Icon name={result.allowed_by_robots ? "check-circle" : "alert-triangle"} className={`size-3.5 ${result.allowed_by_robots ? "text-success" : "text-warning"}`} />
          {result.allowed_by_robots ? t(`${K}.robotsAllowed`) : t(`${K}.robotsBlocked`)}
        </li>
        {result.sitemap_found ? (
          <li className="flex items-center gap-1">
            <Icon name="check-circle" className="size-3.5 text-success" />
            {t(`${K}.sitemapFound`)}
          </li>
        ) : null}
        {result.files > 0 ? <li>{t(`${K}.previewFiles`, { count: result.files })}</li> : null}
      </ul>
      {result.sample.length > 0 ? (
        <ul className="mt-2 space-y-0.5 font-mono text-[11px] text-fg-subtle">
          {result.sample.slice(0, 5).map((url) => (
            <li key={url} className="truncate">
              {url}
            </li>
          ))}
        </ul>
      ) : null}
    </div>
  );
}

/** Website source: URL + scope + include/exclude, with a real crawl-discovery preview first. */
export function WebsitePanel({ formId, audiences, onSubmit, onDiscovered }: PanelProps & { onDiscovered: (pages: number | null) => void }) {
  const { t } = useTranslation();
  const message = useErrorMessage();
  const discover = useDiscoverSite();
  const schema = z.object({
    url: z.string().trim().max(URL_MAX).url(t(`${K}.urlInvalid`)).refine((v) => v.startsWith("https://") || v.startsWith("http://"), t(`${K}.urlInvalid`)),
    maxPages: z.coerce.number().int(),
    include: z.string(),
    exclude: z.string(),
  });
  type Values = z.infer<typeof schema>;
  const { register, handleSubmit, formState } = useForm<Values>({
    resolver: zodResolver(schema),
    defaultValues: { url: "", maxPages: DEFAULT_SCOPE, include: "", exclude: "" },
  });
  const toInput = (values: Values): WebsiteInput => ({
    url: values.url,
    maxPages: values.maxPages,
    include: splitPatterns(values.include),
    exclude: splitPatterns(values.exclude),
    audiences,
  });
  const preview = handleSubmit((values) => {
    discover.mutate(toInput(values), {
      onSuccess: (result) => { onDiscovered(result.page_count); },
      onError: () => { onDiscovered(null); },
    });
  });
  return (
    <form id={formId} noValidate className="space-y-4" onSubmit={(event) => void handleSubmit((values) => { onSubmit({ type: "website", input: toInput(values) }); })(event)}>
      <Field label={t(`${K}.url`)} hint={t(`${K}.urlHint`)} error={formState.errors.url?.message}>
        <Input className="font-mono text-xs" type="url" inputMode="url" placeholder="https://" {...register("url")} />
      </Field>
      <Field label={t(`${K}.scope`)}>
        <Select {...register("maxPages")}>
          {CRAWL_SCOPES.map((scope) => (
            <option key={scope.pages} value={scope.pages}>
              {t(`${K}.${scope.key}`)}
            </option>
          ))}
        </Select>
      </Field>
      <Field label={t(`${K}.include`)}>
        <Input className="font-mono text-xs" placeholder="/help/*, /docs/*" {...register("include")} />
      </Field>
      <Field label={t(`${K}.exclude`)} hint={t(`${K}.patternsHint`)}>
        <Input className="font-mono text-xs" placeholder="/blog/*, *.jpg" {...register("exclude")} />
      </Field>
      <div className="space-y-3">
        <Button type="button" variant="secondary" size="sm" loading={discover.isPending} onClick={() => void preview()}>
          <Icon name="search" />
          {discover.isPending ? t(`${K}.previewing`) : t(`${K}.preview`)}
        </Button>
        {discover.isError ? (
          <p className="text-xs font-semibold text-danger" role="alert">
            {message(discover.error)}
          </p>
        ) : null}
        {discover.data ? <DiscoverySummary result={discover.data} /> : null}
      </div>
    </form>
  );
}
